import React, { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { ChevronRight, ArrowUpRight, Phone, Send, Shield, Sparkles } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';


gsap.registerPlugin(ScrollTrigger);

// ── Stat Counter ────────────────────────────────────────────────────────────────
const Counter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      let start = 0;
      const duration = 1800;
      const step = (end / duration) * 16;
      const timer = setInterval(() => {
        start = Math.min(start + step, end);
        setCount(Math.floor(start));
        if (start >= end) clearInterval(timer);
      }, 16);
      observer.disconnect();
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [end]);
  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

/**
 * SpecialtyPageTemplate
 *
 * @param {object} config
 * @param {string}   config.badge          - Short badge label (e.g. "Cardiac Speciality")
 * @param {string}   config.headline       - Main headline text (before italic)
 * @param {string}   config.headlineItalic - Italic colored portion
 * @param {string}   config.subtext        - Paragraph below headline
 * @param {string}   config.image          - Hero/CTA image path
 * @param {string}   config.imageAlt       - Alt text for the image
 * @param {object[]} config.stats          - [{value, suffix, label}]
 * @param {object[]} config.services       - [{title, icon, desc, specs[], color}]
 * @param {string}   config.sectionTitle   - Section title (e.g. "Our Cardiac")
 * @param {string}   config.sectionItalic  - Italic part (e.g. "Capabilities.")
 * @param {string}   config.warningTitle   - Warning section title
 * @param {string}   config.warningItalic  - Italic part of warning title
 * @param {string}   config.warningText    - Subtext for warning section
 * @param {object[]} config.warnings       - [{name, desc, icon}]
 * @param {string}   config.ctaTitle       - CTA section title
 * @param {string}   config.ctaItalic      - Italic part of CTA title
 * @param {string}   config.ctaText        - CTA body text
 * @param {string}   config.ctaBtn         - CTA primary button text
 * @param {string}   config.seoText        - Footer SEO text
 * @param {string}   [config.ctaBadge]     - Badge for CTA section
 * @param {string}   [config.glowColor]    - Tailwind color for bottom glow e.g. "blue-500"
 */
const SpecialtyPageTemplate = ({ config }) => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.spt-hero-line', { y: 80, opacity: 0 }, {
        y: 0, opacity: 1, stagger: 0.12, duration: 1, ease: 'power3.out', delay: 0.2
      });
      gsap.fromTo('.spt-stat', { y: 40, opacity: 0 }, {
        y: 0, opacity: 1, stagger: 0.1, duration: 0.8, ease: 'power2.out',
        scrollTrigger: { trigger: '.spt-stats-row', start: 'top 80%' }
      });
      gsap.fromTo('.spt-card', { y: 60, opacity: 0, scale: 0.95 }, {
        y: 0, opacity: 1, scale: 1, stagger: 0.15, duration: 0.9, ease: 'power3.out',
        scrollTrigger: { trigger: '.spt-cards-grid', start: 'top 75%' }
      });
      gsap.fromTo('.spt-warning', { x: -30, opacity: 0 }, {
        x: 0, opacity: 1, stagger: 0.1, duration: 0.7, ease: 'power2.out',
        scrollTrigger: { trigger: '.spt-warnings-grid', start: 'top 80%' }
      });
      gsap.fromTo('.spt-cta', { y: 50, opacity: 0 }, {
        y: 0, opacity: 1, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: '.spt-cta-section', start: 'top 75%' }
      });
      gsap.to('.spt-glow', {
        y: '-20%', ease: 'none',
        scrollTrigger: { trigger: '.spt-hero', scrub: true }
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);



  const glowColor = config.glowColor || 'blue-500';

  return (
    <div ref={containerRef} className="bg-white overflow-hidden">

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="spt-hero pt-[140px] md:pt-[160px] pb-24 relative min-h-[92vh] flex flex-col justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={config.image} alt="" className="w-full h-full object-cover opacity-[0.04] blur-2xl scale-125" />
          <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/5" />
        </div>
        <div className="spt-glow absolute top-[15%] right-[8%] w-[500px] h-[500px] bg-primary/8 rounded-full blur-[120px] pointer-events-none" />
        <div className={`absolute bottom-0 left-[15%] w-[350px] h-[350px] bg-${glowColor}/5 rounded-full blur-[100px] pointer-events-none`} />

        <div className="section-container relative z-10">
          <div className="spt-hero-line inline-flex items-center gap-2 px-4 py-2 bg-primary/5 rounded-full border border-primary/15 mb-10">
            <Sparkles size={13} className="text-primary" />
            <span className="text-[10px] font-black tracking-[0.25em] uppercase text-primary">{config.badge}</span>
          </div>
          <div className="max-w-5xl">
            <h1 className="spt-hero-line text-navy font-display font-bold leading-[0.92] tracking-tight text-[clamp(3rem,9vw,7rem)] mb-8">
              {config.headline} <br />
              <em className="font-normal text-primary not-italic">{config.headlineItalic}</em>
            </h1>
            <p className="spt-hero-line text-muted-foreground text-xl md:text-2xl leading-relaxed max-w-2xl mb-12">
              {config.subtext}
            </p>
            <div className="spt-hero-line flex flex-col sm:flex-row gap-4">
              <a href={`https://wa.me/919848954921?text=${encodeURIComponent(`Hi Dr. Suresh, I would like to book an appointment for ${config.badge || 'specialty care'}.`)}`} 
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full font-bold tracking-wide text-sm hover:bg-primary/90 hover:shadow-[0_20px_40px_rgba(3,137,3,0.3)] transition-all duration-500 group">
                <Send size={16} className="group-hover:translate-x-0.5 transition-transform" />
                Book a Consultation
              </a>
              <a href="tel:+919848954921"
                className="inline-flex items-center gap-3 bg-navy/5 text-navy px-8 py-4 rounded-full font-bold tracking-wide text-sm hover:bg-navy/10 transition-all duration-500 border border-navy/10">
                <Phone size={16} className="text-primary" />
                +91 9848954921
              </a>
            </div>
          </div>

          {/* Stats */}
          {config.stats && (
            <div className="spt-stats-row grid grid-cols-2 md:grid-cols-4 gap-4 mt-20 pt-16 border-t border-border/50">
              {config.stats.map((stat, i) => (
                <div key={i} className="spt-stat text-center md:text-left">
                  <p className="text-4xl md:text-5xl font-bold text-navy tabular-nums">
                    <Counter end={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
                  </p>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── SERVICES GRID ─────────────────────────────────────────────── */}
      <section className="py-24 bg-[#f8faff] relative">
        <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent h-24 pointer-events-none" />
        <div className="section-container">
          <div className="max-w-2xl mb-16">
            <p className="text-primary font-black uppercase tracking-[0.2em] text-[11px] mb-4">Clinical Services</p>
            <h2 className="text-navy font-bold text-4xl md:text-5xl leading-tight">
              {config.sectionTitle} <em className="font-normal text-primary not-italic">{config.sectionItalic}</em>
            </h2>
          </div>
          <div className="spt-cards-grid grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {config.services.map((service, i) => (
              <div key={i} className={`spt-card group relative bg-white border border-navy/5 rounded-[2.5rem] p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-700 overflow-hidden`}>
                <div className={`absolute -top-10 -right-10 w-40 h-40 bg-gradient-to-br ${service.color || 'from-primary/5 to-blue-500/5'} rounded-full blur-[60px] opacity-50 group-hover:opacity-100 transition-opacity duration-700`} />
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mb-8 group-hover:bg-primary group-hover:text-white transition-all duration-500">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-navy mb-4 group-hover:text-primary transition-colors duration-500">{service.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-8">{service.desc}</p>
                  <ul className="space-y-3">
                    {service.specs.map((spec, j) => (
                      <li key={j} className="flex items-center justify-between text-[11px] font-bold uppercase tracking-widest text-muted-foreground/70 border-b border-border/50 pb-3 last:border-0 group-hover:text-navy transition-colors duration-300">
                        {spec}
                        <ChevronRight size={12} className="opacity-0 group-hover:opacity-100 text-primary transition-all duration-300 group-hover:translate-x-1" />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WARNINGS / CONDITIONS ─────────────────────────────────────── */}
      <section className="py-24 bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <p className="text-primary font-black uppercase tracking-[0.2em] text-[11px] mb-5">Warning Signs</p>
              <h2 className="text-navy font-bold text-4xl md:text-5xl leading-tight mb-6">
                {config.warningTitle} <br /><em className="font-normal text-primary not-italic">{config.warningItalic}</em>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-10">{config.warningText}</p>
              <Link to="/contact" className="inline-flex items-center gap-3 text-primary font-bold text-sm hover:gap-5 transition-all duration-500 group">
                Book a Screening Today <ArrowUpRight size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
            <div className="spt-warnings-grid grid grid-cols-1 sm:grid-cols-2 gap-4">
              {config.warnings.map((w, i) => (
                <div key={i} className="spt-warning group p-6 bg-[#f8faff] rounded-[2rem] border border-navy/5 hover:border-primary/20 hover:shadow-lg hover:-translate-y-1 transition-all duration-500">
                  {w.icon && <div className="text-3xl mb-4">{w.icon}</div>}
                  <h4 className="font-bold text-navy text-base mb-2 group-hover:text-primary transition-colors">{w.name}</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">{w.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <section className="spt-cta-section py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#010816]" />
        <div className="absolute top-[-20%] left-[-5%] w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-blue-900/15 rounded-full blur-[150px] pointer-events-none" />

        <div className="section-container relative z-10 spt-cta">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-10">
                <Shield size={13} className="text-primary" />
                <span className="text-[10px] font-black tracking-[0.25em] uppercase text-primary">{config.ctaBadge || 'Expert Team'}</span>
              </div>
              <h2 className="text-white font-bold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
                {config.ctaTitle} <br /><em className="font-normal text-primary not-italic">{config.ctaItalic}</em>
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-12 max-w-lg">{config.ctaText}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`https://wa.me/919848954921?text=${encodeURIComponent(`Hi Dr. Suresh, I would like to book an appointment for ${config.badge || 'specialty care'}.`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-primary text-white px-8 py-4 rounded-full font-bold text-sm tracking-wide hover:bg-primary/90 hover:shadow-[0_20px_40px_rgba(3,137,3,0.4)] transition-all duration-500 group">
                  <Send size={16} className="group-hover:translate-x-0.5 transition-transform" />
                  {config.ctaBtn}
                </a>
                <a href="tel:+919848954921"
                  className="inline-flex items-center gap-3 bg-white/5 text-white px-8 py-4 rounded-full font-bold text-sm tracking-wide hover:bg-white/10 transition-all duration-500 border border-white/10">
                  <Phone size={16} className="text-primary" />
                  Emergency: 24/7
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-[3rem] overflow-hidden border border-white/10 shadow-2xl group">
                <img
                  src={config.image}
                  alt={config.imageAlt}
                  className="w-full aspect-square object-contain bg-white/5 p-10 group-hover:scale-105 transition-transform duration-[1500ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#010816]/80 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-primary text-white px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-xl">
                Hyderabad's Premier<br />Clinical Sanctuary
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Footer */}
      <footer className="py-10 opacity-30">
        <div className="section-container text-center">
          <p className="text-[9px] font-bold uppercase tracking-[0.4em] max-w-3xl mx-auto leading-relaxed">
            {config.seoText}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SpecialtyPageTemplate;
